import { useEffect, useState, type ReactNode } from 'react';
import { buildLoginUrl, getIdentity } from '@sdc/auth';
import { Splash } from './Splash';

/**
 * Session-Gate mit eigener Logik statt `RequireAuth`.
 *
 * `RequireAuth` rendert ohne Session `null` und setzt `location.href` auf den
 * Hub-Login. Hat der Hub eine Session, schickt er zurück – und wenn diese App
 * die Session trotzdem nicht lesen kann, entsteht eine **Weiterleitungs-
 * schleife**: leerer Bildschirm, nichts in der Konsole, keine Ursache zu sehen.
 * Genau das ist hier passiert.
 *
 * Deshalb: einmal automatisch zum Hub (Merker in `sessionStorage`), und wenn wir
 * danach wieder ohne Session hier landen, wird der Zustand **angezeigt** statt
 * erneut weitergeleitet. Ein Bildschirm, der seinen Zustand nennt, ist immer
 * besser als einer, der es nochmal versucht.
 */
type State =
  | { kind: 'loading' }
  | { kind: 'authed' }
  | { kind: 'anon' }
  | { kind: 'error'; message: string };

const TRIED_KEY = 'sdc-sales-login-attempted';

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="page">
      <div className="bg-calm" aria-hidden="true" />
      <div className="bg__scanlines" aria-hidden="true" />
      <main className="page__main">
        <div className="container stack">
          <div className="card">
            <h1>{title}</h1>
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}


export function SessionGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let active = true;
    const identity = getIdentity();

    const apply = (session: unknown, error?: { message: string }) => {
      if (!active) return;
      if (error) {
        // Sichtbar UND im Log – vorher gab es zu beidem nichts.
        console.error('[sales] Session konnte nicht gelesen werden:', error.message);
        setState({ kind: 'error', message: error.message });
        return;
      }
      setState(session ? { kind: 'authed' } : { kind: 'anon' });
    };

    identity.auth
      .getSession()
      .then(({ data, error }) => apply(data.session, error ?? undefined))
      .catch((e: unknown) => apply(null, { message: e instanceof Error ? e.message : String(e) }));

    const { data: sub } = identity.auth.onAuthStateChange((_e, session) => apply(session));
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (state.kind !== 'anon') return;
    if (state.kind === 'anon') {
      // Genau ein automatischer Versuch, danach nur noch anzeigen.
      const tried = sessionStorage.getItem(TRIED_KEY);
      if (!tried) {
        sessionStorage.setItem(TRIED_KEY, '1');
        window.location.href = buildLoginUrl(window.location.href);
      }
    }
  }, [state]);

  useEffect(() => {
    if (state.kind === 'authed') sessionStorage.removeItem(TRIED_KEY);
  }, [state]);

  if (state.kind === 'loading') return <Splash label="Sitzung wird geprüft …" />;

  if (state.kind === 'error') {
    return (
      <Card title="Sitzung nicht lesbar">
        <p className="card__meta">{state.message}</p>
        <p style={{ marginTop: '0.75rem' }}>
          <a className="px-btn" href={buildLoginUrl(window.location.href)}>
            Am Hub anmelden
          </a>
        </p>
      </Card>
    );
  }

  if (state.kind === 'anon') {
    return (
      <Card title="Nicht angemeldet">
        <p className="card__meta">
          Diese App liest die Anmeldung aus dem Hub. Hier ist keine Sitzung sichtbar. Häufigste
          Ursachen: die Sitzung ist abgelaufen, oder der Browser blockiert Cookies von
          <span className="mono"> smiling-data-club.de</span> (Drittanbieter-Cookies oder
          Tracking-Schutz).
        </p>
        <p style={{ marginTop: '0.75rem' }}>
          <a className="px-btn" href={buildLoginUrl(window.location.href)}>
            Am Hub anmelden
          </a>
        </p>
      </Card>
    );
  }

  return <>{children}</>;
}
